import { createContext, useContext } from 'react';

/**
 * Cross-mode navigation: lets the Inspector jump from a QC issue to its cue in
 * the Edit mode. The AppShell owns the route state, so it provides the value
 * (switch to Edit + record the pending jump); the Editor consumes the pending
 * index, hands it to the CueTable's `scrollToIndex`, and clears it afterwards.
 * Kept component-free so the provider (inline in AppShell) can satisfy
 * react-refresh's components-only rule.
 */
export interface NavValue {
  /** Switch to the Edit mode and focus the cue with this 1-based index. */
  goToCue: (index: number) => void;
  /** Cue index the editor should bring into view, else null. */
  pendingCueJump: number | null;
  /** Mark the pending jump consumed (re-arms `goToCue` for the same cue). */
  clearCueJump: () => void;
}

export const NavContext = createContext<NavValue | null>(null);

/**
 * Access cross-mode navigation. Returns null outside a provider — the
 * Inspector and Editor also render without one (tests, isolated mounts) and
 * degrade to their non-navigating behaviour.
 */
export function useNav(): NavValue | null {
  return useContext(NavContext);
}
